import { z } from "zod";
import { disqualificationReasonSchema } from "./conversions";
import type { LeadStage, TemplateStatus } from "./domain";
import { leadCloseStageSchema } from "./lead-archive";
import {
  startOfNextZonedDay,
  startOfZonedDate,
  weekdayOf,
  zonedParts,
  zonedTimeToInstant,
} from "./zoned-time";

export const FOLLOWUP_ORIGINS = ["rule", "ai", "member"] as const;
export type FollowupOrigin = (typeof FOLLOWUP_ORIGINS)[number];
export const FOLLOWUP_KINDS = ["followup", "decide"] as const;
export type FollowupKind = (typeof FOLLOWUP_KINDS)[number];
export const FOLLOWUP_VARIANTS = ["generic", "preview", "custom"] as const;
export type FollowupVariant = (typeof FOLLOWUP_VARIANTS)[number];
export const FOLLOWUP_STATUSES = ["pending", "done", "dismissed", "superseded"] as const;
export type FollowupStatus = (typeof FOLLOWUP_STATUSES)[number];

/** Tag (in `custom_fields.tags`) of a lead that stopped answering: no more follow-ups. */
export const FOLLOWUP_COLD_TAG = "freddo";

const CLOSED_STAGES: readonly LeadStage[] = ["won", "lost", "unqualified", "archived"];

/**
 * An upsell opened with a customer (ADR 0065), in `custom_fields.upsell`:
 * while it is on, the customer's unanswered messages get follow-ups, listed
 * apart from the ones that win new customers.
 */
export const leadUpsellSchema = z
  .object({
    since: z.string().datetime({ offset: true }),
    /** What is on offer, in the operator's words ("campagne social"). */
    note: z.string().max(200).nullable(),
    memberId: z.string().uuid().nullable(),
  })
  .strict();
export type LeadUpsell = z.infer<typeof leadUpsellSchema>;

export function readLeadUpsell(
  customFields: Record<string, unknown> | null | undefined,
): LeadUpsell | null {
  const parsed = leadUpsellSchema.safeParse(customFields?.upsell);
  return parsed.success ? parsed.data : null;
}

export const leadUpsellInputSchema = z
  .object({
    version: z.number().int().positive(),
    on: z.boolean(),
    note: z
      .string()
      .trim()
      .max(200)
      .nullable()
      .optional()
      .transform((value) => value || null),
  })
  .strict();
export type LeadUpsellInput = z.input<typeof leadUpsellInputSchema>;

/**
 * Won, lost, unqualified, archived or cold leads get no follow-ups, except a
 * customer with an upsell on (ADR 0065).
 */
export function isFollowupClosedLead(lead: {
  stage: LeadStage;
  tags: readonly unknown[];
  archived: boolean;
  /** The lead has an upsell on; leaving it out keeps customers closed. */
  upsell?: boolean;
}): boolean {
  return (
    lead.archived ||
    (CLOSED_STAGES.includes(lead.stage) && !(lead.stage === "won" && lead.upsell)) ||
    lead.tags.some(
      (tag) => typeof tag === "string" && tag.trim().toLowerCase() === FOLLOWUP_COLD_TAG,
    )
  );
}

export const followupCadenceSchema = z
  .object({
    /** First follow-up, hours after our last unanswered message. */
    firstHours: z.number().int().min(1).max(720),
    /** Second follow-up (and each "riprova"), days after the previous one was sent. */
    secondDays: z.number().int().min(1).max(90),
    /** "Da decidere", days after the second follow-up was sent. */
    decideDays: z.number().int().min(1).max(90),
  })
  .strict();
export type FollowupCadence = z.infer<typeof followupCadenceSchema>;

export const DEFAULT_FOLLOWUP_CADENCE: FollowupCadence = {
  firstHours: 48,
  secondDays: 7,
  decideDays: 7,
};

export function readFollowupCadence(data: Record<string, unknown> | null | undefined) {
  const parsed = followupCadenceSchema.safeParse(data?.followupCadence);
  return parsed.success ? parsed.data : DEFAULT_FOLLOWUP_CADENCE;
}

/**
 * Position in the rule cadence: 1 and 2 are the follow-ups, 3 is "Da decidere";
 * after a "riprova" the even steps are new attempts and the odd ones decisions.
 */
export function followupKindForStep(step: number): FollowupKind {
  return step >= 3 && step % 2 === 1 ? "decide" : "followup";
}

/**
 * A follow-up is "da fare" from the start of its day in the workspace zone
 * (ADR 0055): it shows in the chat, its text is prepared and any message of
 * ours that day completes it.
 */
export function isFollowupActionable(dueAt: Date, now: Date, timeZone: string): boolean {
  return dueAt.getTime() < startOfNextZonedDay(now, timeZone).getTime();
}

/** "Sposta" goes from tomorrow to six months ahead: further is a misunderstanding. */
export const FOLLOWUP_RESCHEDULE_MAX_DAYS = 183;

/** The day "Sposta" moves a follow-up to, or null when it is not between tomorrow and six months. */
export function followupRescheduleDue(
  date: string | null,
  now: Date,
  timeZone: string,
): Date | null {
  const dueAt = date ? startOfZonedDate(date, timeZone) : null;
  if (!dueAt || dueAt < startOfNextZonedDay(now, timeZone)) return null;
  const latest = new Date(now.getTime() + FOLLOWUP_RESCHEDULE_MAX_DAYS * 24 * 60 * 60 * 1_000);
  return dueAt > latest ? null : dueAt;
}

/**
 * Standard texts of the follow-ups, as WhatsApp templates (ADR 0055): copied
 * by hand until Meta approves them, then also sent through the API.
 * `pl_template.purpose` says which one a row is. The first four are chosen on
 * their own for leads who never wrote; the others only from the card's menu
 * (ADR 0061).
 */
export const FOLLOWUP_TEMPLATE_PURPOSES = [
  "followup_first",
  "followup_first_short",
  "followup_preview",
  "followup_last",
  "followup_preview_last",
  "followup_agreed",
] as const;
export type FollowupTemplatePurpose = (typeof FOLLOWUP_TEMPLATE_PURPOSES)[number];

/**
 * `pl_template.purpose` of a follow-up template added from the Templates page
 * (ADR 0061): offered in the card's menu, never chosen on its own.
 */
export const FOLLOWUP_CUSTOM_PURPOSE = "followup";

/** A template the follow-up cards can load: a standard text or one added by the owner. */
export function isFollowupTemplatePurpose(purpose: string): boolean {
  return (
    purpose === FOLLOWUP_CUSTOM_PURPOSE ||
    (FOLLOWUP_TEMPLATE_PURPOSES as readonly string[]).includes(purpose)
  );
}

/** What a positional variable `{{n}}` of a follow-up template is filled with. */
export const TEMPLATE_VARIABLE_SOURCES = ["lead.company", "lead.previewLink"] as const;
export type TemplateVariableSource = (typeof TEMPLATE_VARIABLE_SOURCES)[number];

/** A sample Meta can review, for a variable filled with this lead data. */
export const TEMPLATE_VARIABLE_EXAMPLES: Record<TemplateVariableSource, string> = {
  "lead.company": "Pizzeria Da Mario",
  "lead.previewLink": "https://pizzeria-da-mario.preview.sityweb.it",
};

export const templateVariableSchema = z
  .object({
    /** Lead data that fills it; null for a template filled by hand in the chat. */
    source: z.enum(TEMPLATE_VARIABLE_SOURCES).nullable().default(null),
    /** Sample value Meta requires to review a template with variables. */
    example: z.string().trim().min(1).max(200),
  })
  .strict();
export type TemplateVariable = z.infer<typeof templateVariableSchema>;

/** `pl_template.variable_schema`: the meaning of `{{1}}`, `{{2}}`, … in order. */
export const templateVariablesSchema = z
  .object({ variables: z.array(templateVariableSchema).max(10) })
  .strict();

export function readTemplateVariables(value: unknown): TemplateVariable[] {
  const parsed = templateVariablesSchema.safeParse(value);
  return parsed.success ? parsed.data.variables : [];
}

export type FollowupTemplateDefinition = {
  purpose: FollowupTemplatePurpose;
  /** Name on Meta: lowercase letters, digits and underscores. */
  name: string;
  label: string;
  body: string;
  variables: TemplateVariable[];
};

/**
 * Texts approved by the owner on 2026-10-09. No first name: the Lead Ads
 * forms do not ask for it, and a template cannot drop an empty variable. The
 * draft link is followed by text because Meta rejects a body that starts or
 * ends with a variable.
 */
export const DEFAULT_FOLLOWUP_TEMPLATES: readonly FollowupTemplateDefinition[] = [
  {
    purpose: "followup_first",
    name: "sityweb_followup_1",
    label: "Primo sollecito",
    body: "Ciao 😊 ti riscrivo solo per capire se il progetto del sito per {{1}} è ancora di tuo interesse. Anche due righe vanno benissimo.",
    variables: [{ source: "lead.company", example: "Pizzeria Da Mario" }],
  },
  {
    purpose: "followup_first_short",
    name: "sityweb_followup_1_breve",
    label: "Primo sollecito senza nome attività",
    body: "Ciao 😊 ti riscrivo solo per capire se il progetto del sito è ancora di tuo interesse. Anche due righe vanno benissimo.",
    variables: [],
  },
  {
    purpose: "followup_preview",
    name: "sityweb_followup_bozza",
    label: "Primo sollecito dopo la bozza",
    body: "Ciao 😊 ti riscrivo solo per sapere se hai avuto modo di dare un'occhiata alla bozza del sito 👉 {{1}}\n\nFammi sapere cosa ne pensi, anche due righe vanno benissimo.",
    variables: [
      { source: "lead.previewLink", example: "https://pizzeria-da-mario.preview.sityweb.it" },
    ],
  },
  {
    purpose: "followup_last",
    name: "sityweb_followup_ultimo",
    label: "Ultimo sollecito",
    body: "Ciao 😊 ti scrivo un'ultima volta per chiudere correttamente la richiesta. Se più avanti vorrai riprendere il progetto, scrivimi pure e ripartiamo da qui 👍",
    variables: [],
  },
  // Drafts of 2026-10-15 (ADR 0061), to be reviewed by the owner before Meta.
  {
    purpose: "followup_preview_last",
    name: "sityweb_followup_bozza_ultimo",
    label: "Ultimo sollecito dopo la bozza",
    body: "Ciao 😊 ti scrivo un'ultima volta per la bozza del sito che ti avevo preparato 👉 {{1}}\n\nTra qualche giorno la togliamo dal server per fare spazio ai nuovi progetti: se ti interessa ancora, scrivimi e la teniamo da parte per te.",
    variables: [
      { source: "lead.previewLink", example: "https://pizzeria-da-mario.preview.sityweb.it" },
    ],
  },
  {
    purpose: "followup_agreed",
    name: "sityweb_followup_accordi",
    label: "Ricontatto concordato",
    body: "Ciao 😊 come ci eravamo detti, ti riscrivo per il progetto del sito. Fammi sapere come vuoi procedere, anche due righe vanno benissimo.",
    variables: [],
  },
];

export type FollowupTemplateValues = { company: string | null; previewLink: string | null };

/** Which standard text a follow-up uses: the last step, after a draft, with or without the business. */
export function followupTemplatePurpose(
  followup: { step: number | null; variant: FollowupVariant },
  values: FollowupTemplateValues,
): FollowupTemplatePurpose {
  if (followup.step === 2) return "followup_last";
  if (followup.variant !== "generic" && values.previewLink) return "followup_preview";
  return normalizeBusinessName(values.company) ? "followup_first" : "followup_first_short";
}

const LOWERCASE_PARTICLES = new Set([
  "a",
  "ad",
  "agli",
  "ai",
  "al",
  "all",
  "alla",
  "alle",
  "allo",
  "con",
  "d",
  "da",
  "dal",
  "dall",
  "dalla",
  "dei",
  "del",
  "dell",
  "della",
  "delle",
  "dello",
  "di",
  "e",
  "ed",
  "fra",
  "gli",
  "i",
  "il",
  "in",
  "l",
  "la",
  "le",
  "lo",
  "nel",
  "nell",
  "nella",
  "per",
  "su",
  "sul",
  "sull",
  "sulla",
  "tra",
  "un",
  "una",
  "uno",
]);
const UPPERCASE_WORDS = new Set(["srl", "srls", "snc", "sas", "spa", "b&b", "bnb"]);

/**
 * Answers to the form's "nome dell'attività" that are not a business: the lead
 * wrote about themselves, or nothing (owner, 21/10: «il progetto del sito per
 * Io»). Compared with the whole answer, lowercase and without accents or
 * punctuation, so a business that only contains one of them ("Io e Te
 * Pizzeria") keeps its name.
 */
const NOT_A_BUSINESS = new Set([
  "io",
  "io stesso",
  "io stessa",
  "sono io",
  "me",
  "me stesso",
  "me stessa",
  "noi",
  "privato",
  "privata",
  "privati",
  "cliente privato",
  "persona fisica",
  "libero professionista",
  "libera professionista",
  "professionista",
  "lavoratore autonomo",
  "lavoratrice autonoma",
  "autonomo",
  "autonoma",
  "freelance",
  "partita iva",
  // Romanian, "self-employed": seen in a form of 20/10.
  "propriul angajat",
  "nessuna",
  "nessuno",
  "niente",
  "nulla",
  "non ho",
  "non ce l ho",
  "non ancora",
  "ancora nessuna",
  "ancora niente",
  "da definire",
  "da decidere",
  "non so",
  "boh",
  "test",
  "prova",
  "italia",
  "italy",
]);

/**
 * Whether a form answer can stand for the business in "il sito per …": not
 * three letters or fewer ("Io", "Ofr"), not an answer of `NOT_A_BUSINESS`, not
 * a place given with its country ("Pavia, Italia"). An organization stays even
 * when it may be the employer ("Ministero della pubblica istruzione"): only a
 * guess could tell, and the operator reads the text before it leaves.
 */
export function isBusinessName(text: string): boolean {
  if ((text.match(/\p{L}/gu) ?? []).length <= 3) return false;
  if (/,\s*(italia|italy)$/iu.test(text.trim())) return false;
  const answer = text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("it")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
  return !NOT_A_BUSINESS.has(answer);
}

/**
 * The business name of the form as a template value: spaces collapsed (Meta
 * refuses line breaks and long runs of spaces), and a name typed all in
 * lowercase or uppercase gets capital initials. Mixed case stays as typed. An
 * answer that is not a business (`isBusinessName`) gives null: the follow-up
 * then uses its text without the name.
 */
export function normalizeBusinessName(value: string | null | undefined): string | null {
  const text = value?.replace(/\s+/gu, " ").trim();
  if (!text || !isBusinessName(text)) return null;
  const lower = text.toLocaleLowerCase("it");
  if (!/\p{L}/u.test(text) || (text !== lower && text !== text.toLocaleUpperCase("it"))) {
    return text;
  }
  let first = true;
  return lower
    .split(" ")
    .map((word) => {
      if (UPPERCASE_WORDS.has(word)) {
        first = false;
        return word.toLocaleUpperCase("it");
      }
      return word
        .split(/(['’])/u)
        .map((part) => {
          if (part === "'" || part === "’" || !part) return part;
          const keep = !first && LOWERCASE_PARTICLES.has(part);
          first = false;
          return keep ? part : part.charAt(0).toLocaleUpperCase("it") + part.slice(1);
        })
        .join("");
    })
    .join(" ");
}

/** Values of the template variables in order, or null when one is missing. */
export function followupTemplateParameters(
  variables: readonly Pick<TemplateVariable, "source">[],
  values: FollowupTemplateValues,
): string[] | null {
  const parameters = variables.map((variable) =>
    variable.source === "lead.company"
      ? normalizeBusinessName(values.company)
      : variable.source === "lead.previewLink"
        ? values.previewLink?.trim() || null
        : null,
  );
  return parameters.every((parameter): parameter is string => Boolean(parameter))
    ? parameters
    : null;
}

/** The body with `{{n}}` replaced by the parameters: what WhatsApp shows. */
export function fillTemplateBody(body: string, parameters: readonly string[]): string {
  return body.replace(
    /\{\{(\d+)\}\}/g,
    (placeholder, index: string) => parameters[Number(index) - 1] ?? placeholder,
  );
}

/** A follow-up template of the workspace, or the built-in text when the workspace has none. */
export type FollowupTemplateRef = {
  /** `pl_template.id`; null for the built-in text. */
  id: string | null;
  name: string;
  status: TemplateStatus | "builtin";
  body: string;
  variables: readonly TemplateVariable[];
};

export function builtinFollowupTemplate(purpose: FollowupTemplatePurpose): FollowupTemplateRef {
  const definition = DEFAULT_FOLLOWUP_TEMPLATES.find((item) => item.purpose === purpose);
  if (!definition) throw new Error(`Unknown follow-up template ${purpose}`);
  return {
    id: null,
    name: definition.name,
    status: "builtin",
    body: definition.body,
    variables: definition.variables,
  };
}

/**
 * The template of a follow-up and its text. A template whose values are
 * missing gives way to the short first text, which has none.
 */
export function chooseFollowupTemplate(
  templates: Partial<Record<FollowupTemplatePurpose, FollowupTemplateRef>>,
  followup: { step: number | null; variant: FollowupVariant },
  values: FollowupTemplateValues,
): {
  purpose: FollowupTemplatePurpose;
  template: FollowupTemplateRef;
  parameters: string[];
  text: string;
} {
  const preferred = followupTemplatePurpose(followup, values);
  for (const purpose of [preferred, "followup_first_short"] as const) {
    for (const template of [templates[purpose], builtinFollowupTemplate(purpose)]) {
      if (!template) continue;
      const parameters = followupTemplateParameters(template.variables, values);
      if (parameters) {
        return { purpose, template, parameters, text: fillTemplateBody(template.body, parameters) };
      }
    }
  }
  const template = builtinFollowupTemplate("followup_first_short");
  return { purpose: "followup_first_short", template, parameters: [], text: template.body };
}

/**
 * Every text this lead's follow-up can load from the card's menu (ADR 0061),
 * filled for the lead: the standard ones, then the follow-up templates added
 * from the Templates page. One whose values are missing is left out.
 */
export function followupTemplateChoices(
  templates: Partial<Record<FollowupTemplatePurpose, FollowupTemplateRef>>,
  values: FollowupTemplateValues,
  custom: readonly FollowupTemplateRef[] = [],
): Array<{
  /** The menu's key: the purpose of a standard text, `template:<id>` for the others. */
  key: string;
  purpose: FollowupTemplatePurpose | null;
  label: string;
  template: FollowupTemplateRef;
  parameters: string[];
  text: string;
}> {
  const standard = DEFAULT_FOLLOWUP_TEMPLATES.map((definition) => ({
    key: definition.purpose as string,
    purpose: definition.purpose as FollowupTemplatePurpose | null,
    label: definition.label,
    template: templates[definition.purpose] ?? builtinFollowupTemplate(definition.purpose),
  }));
  const added = custom.map((template) => ({
    key: `template:${template.id}`,
    purpose: null,
    label: template.name,
    template,
  }));
  return [...standard, ...added].flatMap((choice) => {
    const parameters = followupTemplateParameters(choice.template.variables, values);
    if (!parameters) return [];
    return [{ ...choice, parameters, text: fillTemplateBody(choice.template.body, parameters) }];
  });
}

const PREVIEW_URL_PATTERN =
  /(?:https?:\/\/)?[a-z0-9-]+\.preview\.sityweb\.it(?![a-z0-9-]|\.[a-z0-9])(?:\/[^\s]*)?/i;

/** The Sityweb preview link written in a message, as an https URL. */
export function extractPreviewLink(body: string | null | undefined): string | null {
  const match = body?.match(PREVIEW_URL_PATTERN)?.[0];
  if (!match) return null;
  const link = match.replace(/[).,;:!?»"'’]+$/u, "");
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
}

export const FOLLOWUP_QUICK_CHOICES = ["tomorrow", "in3days", "nextWeek", "endOfMonth"] as const;
export type FollowupQuickChoice = (typeof FOLLOWUP_QUICK_CHOICES)[number];

const QUICK_CHOICE_HOUR = 9;

/**
 * Dates offered by "Programma follow-up", at 9:00 in the workspace zone:
 * tomorrow, in three days, next Monday, last working day of the month (Saturday
 * counts). Sunday always becomes Monday.
 */
export function followupQuickChoiceDate(
  choice: FollowupQuickChoice,
  now: Date,
  timeZone: string,
): Date {
  const { year, month, day, weekday } = zonedParts(now, timeZone);
  const at9 = (y: number, m: number, d: number) =>
    zonedTimeToInstant(
      {
        year: y,
        month: m,
        day: weekdayOf(y, m, d) === 0 ? d + 1 : d,
        hour: QUICK_CHOICE_HOUR,
        minute: 0,
      },
      timeZone,
    );
  if (choice === "tomorrow") return at9(year, month, day + 1);
  if (choice === "in3days") return at9(year, month, day + 3);
  if (choice === "nextWeek") return at9(year, month, day + ((8 - weekday) % 7 || 7));
  const endOfMonth = (y: number, m: number) => {
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return at9(y, m, weekdayOf(y, m, last) === 0 ? last - 1 : last);
  };
  const thisMonth = endOfMonth(year, month);
  return thisMonth > now ? thisMonth : endOfMonth(month === 12 ? year + 1 : year, (month % 12) + 1);
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === undefined ? undefined : value || null));

export const followupScheduleInputSchema = z
  .object({
    dueAt: z.string().datetime({ offset: true }),
    note: optionalText(1_000),
    /** Text to send; empty keeps the workspace text. */
    content: optionalText(4_096),
  })
  .strict();
export type FollowupScheduleInput = z.input<typeof followupScheduleInputSchema>;

export const followupPatchSchema = z
  .object({
    version: z.number().int().positive(),
    dueAt: z.string().datetime({ offset: true }).optional(),
    note: optionalText(1_000),
    content: optionalText(4_096),
  })
  .strict();
export type FollowupPatch = z.input<typeof followupPatchSchema>;

export const followupVersionInputSchema = z
  .object({ version: z.number().int().positive() })
  .strict();

/**
 * "Modifica con AI" on a follow-up (ADR 0061): the text in the card and what
 * to change, empty to adapt it to the chat. Without a text the AI decides
 * from the chat alone, as the morning pass does.
 */
export const followupPrepareInputSchema = z
  .object({
    version: z.number().int().positive(),
    text: optionalText(4_096),
    instructions: optionalText(2_000),
  })
  .strict();
export type FollowupPrepareInput = z.input<typeof followupPrepareInputSchema>;

/** "Da decidere": archive, mark cold, try again; on an upsell, close it (ADR 0065). */
export const FOLLOWUP_DECISIONS = ["archive", "cold", "retry", "close_upsell"] as const;
export type FollowupDecision = (typeof FOLLOWUP_DECISIONS)[number];

export const followupDecisionInputSchema = z
  .object({
    version: z.number().int().positive(),
    action: z.enum(FOLLOWUP_DECISIONS),
    /** Only with "archive": the stage that says why (ADR 0070). */
    closeAs: leadCloseStageSchema.optional(),
    disqualificationReason: disqualificationReasonSchema.optional(),
    disqualificationNote: z.string().trim().max(1000).optional(),
  })
  .strict()
  .refine((input) => input.closeAs === undefined || input.action === "archive", {
    message: "closeAs vale solo per l'archiviazione",
    path: ["closeAs"],
  });
